import { useTranslation } from 'react-i18next';

const CERT_HREFS = [
  'https://www.globalagilitytools.com/tip-certification',
  'https://www.globalagilitytools.com/ipi-certification',
  'https://www.ebrd.com/home.html',
  'https://www.ebrd.com/home.html',
  'https://www.groupanalysis.org/',
  'https://hbsp.harvard.edu/harvard-manage-mentor/',
];

const CERT_ICONS = [
  'ri-global-line',
  'ri-compass-discover-line',
  'ri-seedling-line',
  'ri-loop-left-line',
  'ri-group-line',
  'ri-graduation-cap-line',
];

const CERT_YEARS = ['2025', '2025', '2020', '2018', '2017', '2015'];

const DEGREE_ICONS = ['ri-mental-health-line', 'ri-briefcase-line', 'ri-graduation-cap-line'];

export default function EducationCertifications() {
  const { t } = useTranslation();

  const degrees = [
    { degreeKey: 'education.d1Degree', institutionKey: 'education.d1Institution', locationKey: 'education.d1Location', periodKey: 'education.d1Period', icon: DEGREE_ICONS[0], hasLocation: true },
    { degreeKey: 'education.d2Degree', institutionKey: 'education.d2Institution', locationKey: '',                     periodKey: 'education.d2Period', icon: DEGREE_ICONS[1], hasLocation: false },
    { degreeKey: 'education.d3Degree', institutionKey: 'education.d3Institution', locationKey: '',                     periodKey: 'education.d3Period', icon: DEGREE_ICONS[2], hasLocation: false },
  ];

  const certs = [
    { titleKey: 'education.cert1Title', issuerKey: 'education.cert1Issuer', icon: CERT_ICONS[0], year: CERT_YEARS[0], href: CERT_HREFS[0] },
    { titleKey: 'education.cert2Title', issuerKey: 'education.cert2Issuer', icon: CERT_ICONS[1], year: CERT_YEARS[1], href: CERT_HREFS[1] },
    { titleKey: 'education.cert3Title', issuerKey: 'education.cert3Issuer', icon: CERT_ICONS[2], year: CERT_YEARS[2], href: CERT_HREFS[2] },
    { titleKey: 'education.cert4Title', issuerKey: 'education.cert4Issuer', icon: CERT_ICONS[3], year: CERT_YEARS[3], href: CERT_HREFS[3] },
    { titleKey: 'education.cert5Title', issuerKey: 'education.cert5Issuer', icon: CERT_ICONS[4], year: CERT_YEARS[4], href: CERT_HREFS[4] },
    { titleKey: 'education.cert6Title', issuerKey: 'education.cert6Issuer', icon: CERT_ICONS[5], year: CERT_YEARS[5], href: CERT_HREFS[5] },
  ];

  return (
    <section className="bg-warm-white py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-14 lg:gap-16 items-start">

          {/* LEFT: Education */}
          <div className="w-full lg:w-[38%] shrink-0">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-[0.2em] text-sage font-inter font-medium">
                {t('education.badge')}
              </span>
              <div className="w-10 h-px bg-sage mt-3" />
            </div>

            <div className="flex flex-col">
              {degrees.map((item, idx) => (
                <div
                  key={item.degreeKey}
                  className={`flex items-start gap-5 py-8 ${idx < degrees.length - 1 ? 'border-b border-sage/10' : ''}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center text-sage shrink-0 mt-0.5">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-inter font-semibold text-sm text-charcoal leading-snug mb-1">
                      {t(item.degreeKey)}
                    </h3>
                    <p className="font-inter text-xs text-text-muted leading-relaxed mb-1">
                      {t(item.institutionKey)}
                      {item.hasLocation ? ` — ${t(item.locationKey)}` : ''}
                    </p>
                    <span className="font-cormorant text-lg font-light text-sage">
                      {t(item.periodKey)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Vertical divider */}
          <div className="hidden lg:block w-px self-stretch bg-sage/10" />

          {/* RIGHT: Certifications */}
          <div className="flex-1 min-w-0">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-[0.2em] text-sage font-inter font-medium">
                {t('education.certsBadge')}
              </span>
              <div className="w-10 h-px bg-sage mt-3" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {certs.map((cert) => (
                <a
                  key={cert.titleKey}
                  href={cert.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-warm-white rounded-xl p-5 border border-sage/50 hover:bg-beige hover:border-beige transition-all duration-300 flex flex-col gap-3 cursor-pointer no-underline"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-7 h-7 flex items-center justify-center text-sage">
                      <i className={`${cert.icon} text-base`} />
                    </div>
                    <span className="text-[10px] font-inter font-medium text-sage bg-canvas group-hover:bg-beige/70 group-hover:text-white px-2.5 py-0.5 rounded-full whitespace-nowrap transition-colors duration-300">
                      {cert.year}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-inter font-semibold text-xs text-charcoal group-hover:text-white leading-snug mb-1 transition-colors duration-300">
                      {t(cert.titleKey)}
                    </h3>
                    <p className="font-inter text-[10px] text-charcoal/60 font-medium group-hover:text-white/80 tracking-wide uppercase transition-colors duration-300">
                      {t(cert.issuerKey)}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>

        </div>

        {/* Academic & Volunteer paragraph */}
        <div className="mt-14 pt-10 border-t border-sage/10 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-sage font-inter font-medium block mb-4">
            {t('education.academicBadge')}
          </span>
          <p className="font-inter text-sm text-charcoal/75 leading-relaxed max-w-3xl mx-auto">
            {t('education.academicText')}
          </p>
        </div>

      </div>
    </section>
  );
}
